/**
 * Reason entry for opening a draft or artifact body.
 *
 * Choosing a category and writing a reason is what the server records in the
 * audit log before the body opens; the dialog says so up front.
 */

import { useState } from "react";
import { LockKeyOpen } from "@phosphor-icons/react";
import { Dialog, DialogContent, DialogFooter } from "@/components/ui/Dialog";
import { Alert } from "@/components/ui/Alert";
import { Button } from "@/components/ui/Button";
import { Spinner } from "@/components/ui/Spinner";
import { ModalHeader } from "@/components/ui/ModalParts";
import type { AdminRevealCategory } from "@/services/admin";
import { cn } from "@/utils/cn";

const REASON_OPTIONS: {
  value: AdminRevealCategory;
  label: string;
  hint: string;
}[] = [
  {
    value: "support_request",
    label: "User support request",
    hint: "The user asked for help. A ticket or reference is required.",
  },
  {
    value: "abuse_investigation",
    label: "Abuse or security investigation",
    hint: "A report or alert that needs the text to assess.",
  },
  {
    value: "legal_demand",
    label: "Legal demand",
    hint: "A request from a court, regulator or authority.",
  },
  {
    value: "other",
    label: "Other",
    hint: "Explain in the reason.",
  },
];

export interface RevealReasonValues {
  category: AdminRevealCategory;
  reason: string;
  reference?: string;
}

const FIELD_CLASS =
  "block w-full rounded-lg border border-input bg-background px-3 py-2 text-sm placeholder:text-muted-foreground focus-visible:border-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/30";

export function RevealReasonDialog({
  open,
  onOpenChange,
  subject,
  minReasonLength,
  windowMinutes,
  isPending,
  error,
  onSubmit,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** What is being opened, e.g. "draft" or "artifact". */
  subject: string;
  minReasonLength: number;
  windowMinutes: number;
  isPending: boolean;
  error: string | null;
  onSubmit: (values: RevealReasonValues) => void;
}) {
  const [category, setCategory] = useState<AdminRevealCategory | null>(null);
  const [reason, setReason] = useState("");
  const [reference, setReference] = useState("");

  const needsReference = category === "support_request";
  const trimmed = reason.trim();
  const reasonOk = trimmed.length >= minReasonLength;
  const referenceOk = !needsReference || reference.trim().length > 0;
  const canSubmit = category !== null && reasonOk && referenceOk && !isPending;

  function handleOpenChange(next: boolean) {
    if (isPending) return;
    if (!next) {
      setCategory(null);
      setReason("");
      setReference("");
    }
    onOpenChange(next);
  }

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!canSubmit || category === null) return;
    onSubmit({
      category,
      reason: trimmed,
      ...(needsReference && { reference: reference.trim() }),
    });
  }

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="max-w-md">
        <form onSubmit={handleSubmit} className="space-y-4">
          <ModalHeader
            icon={<LockKeyOpen className="h-5 w-5" />}
            title={`Reveal ${subject} body`}
            description={`Your name, this reason and the time are written to the audit log before the text opens. It stays open for ${windowMinutes} minutes.`}
          />

          <fieldset className="space-y-2">
            <legend className="text-xs font-medium text-muted-foreground">
              Why do you need to read it?
            </legend>
            {REASON_OPTIONS.map((option) => (
              <label
                key={option.value}
                className={cn(
                  "flex cursor-pointer items-start gap-2.5 rounded-lg border p-2.5 text-sm transition-colors",
                  category === option.value
                    ? "border-primary bg-primary/5"
                    : "border-border hover:bg-muted/40"
                )}
              >
                <input
                  type="radio"
                  name="reveal-category"
                  value={option.value}
                  checked={category === option.value}
                  onChange={() => setCategory(option.value)}
                  className="mt-1"
                />
                <span>
                  <span className="block font-medium">{option.label}</span>
                  <span className="block text-xs text-muted-foreground">
                    {option.hint}
                  </span>
                </span>
              </label>
            ))}
          </fieldset>

          {needsReference && (
            <div className="space-y-1">
              <label
                htmlFor="reveal-reference"
                className="text-xs font-medium text-muted-foreground"
              >
                Ticket or reference
              </label>
              <input
                id="reveal-reference"
                value={reference}
                onChange={(e) => setReference(e.target.value)}
                maxLength={120}
                placeholder="e.g. SUP-1042"
                className={FIELD_CLASS}
              />
            </div>
          )}

          <div className="space-y-1">
            <label
              htmlFor="reveal-reason"
              className="text-xs font-medium text-muted-foreground"
            >
              Reason
            </label>
            <textarea
              id="reveal-reason"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              rows={3}
              maxLength={1000}
              placeholder="What are you checking, and why does it need the text?"
              className={cn(FIELD_CLASS, "resize-none")}
            />
            <p
              className={cn(
                "text-xs",
                reasonOk || trimmed.length === 0
                  ? "text-muted-foreground"
                  : "text-destructive"
              )}
            >
              {trimmed.length}/{minReasonLength} characters minimum
            </p>
          </div>

          {error && <Alert variant="error">{error}</Alert>}

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => handleOpenChange(false)}
              disabled={isPending}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={!canSubmit}>
              {isPending ? <Spinner size="sm" /> : null}
              Reveal body
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
